import { getAllSceneNodes, type CurvaDocument } from "@/document/model/document";
import type { TextNode } from "@/document/model/nodes";
import { rectsEqual, type Rect } from "@/lib/math/rect";

/**
 * Measures the intrinsic bounds of a text node (honoring its textLayout).
 *
 * Text measurement lives in the renderer (canvaskit / DOM), which the document
 * layer cannot depend on directly. The renderer registers a measurer at startup
 * so geometry finalization can keep AUTO_WIDTH / AUTO_HEIGHT text in sync with
 * its content — including during auto-layout, not only on text edits.
 */
export type TextBoundsMeasurer = (node: TextNode) => Rect;

let measurer: TextBoundsMeasurer | undefined;

export function registerTextBoundsMeasurer(next: TextBoundsMeasurer | undefined): void {
  measurer = next;
}

export function hasTextBoundsMeasurer(): boolean {
  return measurer !== undefined;
}

/**
 * Reports the visual (ink) bounds of a text node — the tight box around the
 * rendered glyphs, excluding line-height leading and ascent/descent slack.
 *
 * Auto-layout centers text on these bounds (mirroring the right-panel alignment
 * buttons) so glyphs appear optically centered rather than centering the taller
 * line box. Registered by the renderer; absent in headless/tests.
 */
export type TextVisualBoundsProvider = (node: TextNode) => Rect;

let visualBoundsProvider: TextVisualBoundsProvider | undefined;

export function registerTextVisualBoundsProvider(next: TextVisualBoundsProvider | undefined): void {
  visualBoundsProvider = next;
}

export function textVisualBoundsFor(node: TextNode): Rect | undefined {
  return visualBoundsProvider?.(node);
}

/**
 * Re-measures every auto-sized text node and writes back its intrinsic bounds.
 * Returns the ids of text nodes whose bounds actually changed, so callers can
 * feed them into incremental auto-layout (their containers must re-flow).
 *
 * No-op when no measurer is registered (headless / tests) — text bounds are
 * then trusted as-is.
 */
export function refreshTextIntrinsicBounds(document: CurvaDocument): string[] {
  if (!measurer) {
    return [];
  }

  const changed: string[] = [];
  for (const node of getAllSceneNodes(document)) {
    if (node.type !== "text" || node.text.textLayout === "FIXED_SIZE") {
      continue;
    }
    const next = measurer(node);
    if (!rectsEqual(node.bounds, next)) {
      node.bounds = next;
      changed.push(node.id);
    }
  }
  return changed;
}
